import { filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { SchoolDto } from '../models/dto/school.dto';

import { SimpleUserDto } from '../models/dto/simple-user.dto';
import { ProfilesEnum } from '../models/enum/profiles.enum';
import { UserDetailsDto } from '../models/dto/user-details.dto';
import { buildCookieServiceOptions } from './cookie.utils';
import { ADMIN_PROFILES, ADVISOR_PROFILES, COORDINATOR_PROFILES, DIRECTOR_PROFILES, EMPLOYEE_PROFILES, MANAGEMENT_PROFILES, RESPONSABLE_PROFILES, STUDENT_PROFILES, TEACHER_PROFILES } from './profile.utils';

@Injectable({ providedIn: 'root' })
export class UserUtil {
  constructor(private cookieService: CookieService) { }

  getSchools(): SchoolDto[] {
    const user = this.getUser();

    if (!this.hasManySchools() || !this.isAdminProfile()) {
      return user.schools;
    }

    // Importante: condição necessária para utilizar a escola selecionada nos query params dos componentes
    if (this.isAdminProfile() && this.getUserSelectedSchool()) {
      const userSchool = this.getUserSelectedSchool() as SchoolDto;
      return [userSchool];
    } else {
      return user.schools;
    }
  }

  addUser(user: UserDetailsDto) {
    var simpleUser: SimpleUserDto = {
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      profiles: user.profiles,
      schools: user.schools,
      integration: user.integration,
    };

    this.cookieService.set('user', JSON.stringify(simpleUser), buildCookieServiceOptions(false));
  }

  getUser(): SimpleUserDto {
    let user: any;
    let userJson = this.cookieService.get('user');
    if (typeof userJson === 'string' && userJson) user = JSON.parse(userJson);

    return user;
  }

  addUserFcmToken(token: string) {
    this.cookieService.set('userFcmToken', token, { secure: true });
  }

  getUserFcmToken(): string {
    return this.cookieService.get('userFcmToken');
  }

  addUserSelectedSchool(school: SchoolDto): void {
    this.cookieService.set('userSchool', JSON.stringify(school), buildCookieServiceOptions(false));
  }

  getUserSelectedSchool(): SchoolDto | undefined {
    let userSchoolJson = this.cookieService.get('userSchool');

    if (!userSchoolJson) return undefined;

    const userSchool = JSON.parse(userSchoolJson) as SchoolDto;

    return userSchool;
  }

  private get userProfiles(): ProfilesEnum[] {
    return this.getUser()?.profiles as ProfilesEnum[] ?? [];
  }

  private hasAnyProfile(profiles: ProfilesEnum[]): boolean {
    return profiles.some(profile =>
      this.userProfiles.includes(profile),
    );
  }

  isFuncionarioProfile(): boolean {
    return this.hasAnyProfile(EMPLOYEE_PROFILES);
  }

  isResponsablesProfile(): boolean {
    return this.hasAnyProfile(RESPONSABLE_PROFILES);
  }

  isDirectorProfile(): boolean {
    return this.hasAnyProfile(DIRECTOR_PROFILES);
  }

  isProfessorProfile(): boolean {
    return this.hasAnyProfile(TEACHER_PROFILES);
  }

  isChild(): boolean {
    return this.hasAnyProfile(STUDENT_PROFILES);
  }

  isCoordinatorProfile(): boolean {
    return this.hasAnyProfile(COORDINATOR_PROFILES);
  }

  isAssessor(): boolean {
    return this.hasAnyProfile(ADVISOR_PROFILES);
  }

  isAdminProfile(): boolean {
    return this.hasAnyProfile(ADMIN_PROFILES);
  }

  canEditGates(): boolean {
    return this.isDirectorProfile() || this.isCoordinatorProfile();
  }

  hasManySchools(): boolean {
    return this.getUser()?.schools.length > 1;
  }
}
